import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Chip, CircularProgress, Divider, List, ListItem, Typography,
} from '@mui/material';
import { ActivityService } from '../../services/activity.service';
import {
  Activity, ActivityOwnerType, ACTIVITY_TYPE_LABELS,
} from '../../interfaces/crm/crm.interface';
import { useToast } from '../../context/ToastContext';

interface ActivityTimelineProps {
  ownerType: ActivityOwnerType;
  ownerId: string;
  // Bump this to force a re-fetch after logging a new activity.
  reloadKey?: number;
}

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ ownerType, ownerId, reloadKey }) => {
  const toast = useToast();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ActivityService.listByOwner(ownerType, ownerId);
      setActivities(res);
    } catch (e) {
      toast.showApiError(e, 'Failed to load activities');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerType, ownerId]);

  useEffect(() => { load(); }, [load, reloadKey]);

  if (loading) return <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>;

  if (activities.length === 0) {
    return <Typography color="text.secondary" sx={{ p: 2 }}>No activities logged yet.</Typography>;
  }

  return (
    <List disablePadding>
      {activities.map((a, i) => (
        <React.Fragment key={a.id}>
          {i > 0 && <Divider component="li" />}
          <ListItem alignItems="flex-start" data-testid="activity-item" sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
              <Chip label={ACTIVITY_TYPE_LABELS[a.type]} size="small" />
              <Typography variant="caption" color="text.secondary">
                {formatWhen(a.occurredAt)}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{a.note}</Typography>
          </ListItem>
        </React.Fragment>
      ))}
    </List>
  );
};

export default ActivityTimeline;
